/**
 * HelpPanel — the in-app help surface. At 1024px and wider it is a docked, non-modal 400px column that
 * the page reflows around (the same offset the workflow rail uses, which it replaces while open); below
 * that it is a modal bottom sheet with a scrim. Opening, using and closing it never changes the URL or
 * touches the page behind (BR-12). Both forms end above the cookie consent banner and whatever it stacks
 * above (--cookie-consent-covered: the banner plus, on mobile, the bottom tab bar), so the banner never
 * covers the composer or the footers.
 */

import { forwardRef, useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, History, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ArticleList } from './ArticleList';
import { ArticleView } from './ArticleView';
import { AskPanel } from './AskPanel';
import { CategoryCards } from './CategoryCards';
import { articleFocusKey, topicFocusKey } from './focus-keys';
import { useHelp, type HelpTab } from './HelpProvider';
import { HelpSearchField, SearchResults } from './SearchResults';
import { TopicArticles } from './TopicArticles';

/** Width of the docked panel, in rem (400px); the page offsets by the same amount. */
const DOCK_WIDTH_REM = '25rem';

/** Gives focus back to whichever Help button (header or top bar) is showing, so keyboard users keep their place. */
function focusHelpButton() {
  const buttons = document.querySelectorAll<HTMLElement>('button[aria-controls="help-panel"]');
  for (const button of buttons) {
    if (button.offsetParent !== null) {
      button.focus();
      return;
    }
  }
}

const ICON_BUTTON_CLASS =
  'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const IconButton = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    onClick: () => void;
    children: ReactNode;
    'data-testid': string;
  }
>(function IconButton({ label, onClick, children, 'data-testid': testId }, ref) {
  return (
    <button ref={ref} type="button" aria-label={label} onClick={onClick} data-testid={testId} className={ICON_BUTTON_CLASS}>
      {children}
    </button>
  );
});

interface PanelHeaderProps {
  inSheet: boolean;
  /** The panel's title heading: focus lands here when a view opens (see PanelContent). */
  titleRef: RefObject<HTMLHeadingElement>;
  /** The History button: focus returns here when the history view closes. */
  historyButtonRef: RefObject<HTMLButtonElement>;
}

function PanelHeader({ inSheet, titleRef, historyButtonRef }: PanelHeaderProps) {
  const { tab, articleId, topic, historyOpen, pageName, chat, closePanel, closeArticle, closeTopic, setHistoryOpen } = useHelp();

  let title = 'Help';
  let back: { label: string; onClick: () => void } | null = null;
  if (articleId) {
    // The title names the view Back returns to: the topic when opened from one, else the screen's help.
    title = topic ? topic.label : pageName ? `${pageName} help` : 'Help';
    back = {
      label: topic ? `Back to ${topic.label}` : pageName ? `Back to ${pageName} help` : 'Back to help',
      onClick: closeArticle,
    };
  } else if (topic) {
    title = topic.label;
    back = { label: 'Back to help', onClick: closeTopic };
  } else if (historyOpen) {
    title = 'Conversations';
    back = { label: 'Back to chat', onClick: () => setHistoryOpen(false) };
  }

  const titleClass =
    'rounded text-base font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  const showChatActions = tab === 'ask' && !articleId && !topic && !historyOpen;

  return (
    <div className="flex h-14 flex-none items-center justify-between border-b border-border pl-4 pr-2">
      <div className="-ml-3 flex min-w-0 items-center gap-1">
        {back && (
          <IconButton label={back.label} onClick={back.onClick} data-testid="help-back">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        )}
        {inSheet ? (
          <SheetTitle ref={titleRef} tabIndex={-1} className={`${titleClass} truncate ${back ? '' : 'pl-3'}`} data-testid="help-panel-title">
            {title}
          </SheetTitle>
        ) : (
          <h2 ref={titleRef} tabIndex={-1} className={`${titleClass} truncate ${back ? '' : 'pl-3'}`} data-testid="help-panel-title">
            {title}
          </h2>
        )}
      </div>
      <div className="flex items-center">
        {showChatActions && (
          <>
            <IconButton
              ref={historyButtonRef}
              label="Conversation history"
              onClick={() => setHistoryOpen(true)}
              data-testid="help-history-button"
            >
              <History className="h-4 w-4" aria-hidden="true" />
            </IconButton>
            <IconButton
              label="New conversation"
              onClick={() => chat.newConversation()}
              data-testid="help-new-conversation-button"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </>
        )}
        <IconButton label="Close help" onClick={closePanel} data-testid="help-close">
          <X className="h-4 w-4" aria-hidden="true" />
        </IconButton>
      </div>
    </div>
  );
}

const FOOTER_LINK_CLASS = 'text-primary';

function ContactSupportLink() {
  const { closePanel } = useHelp();
  return (
    <Button asChild variant="ghost" className={FOOTER_LINK_CLASS}>
      <Link to="/support" onClick={closePanel} data-testid="help-footer-contact">
        Contact support
      </Link>
    </Button>
  );
}

/** The Articles tab's footer: the full help center and the support form. */
function ArticlesFooter() {
  const { closePanel } = useHelp();
  return (
    <div className="flex flex-none items-center justify-between border-t border-border px-4 py-1">
      <Button asChild variant="ghost" className={FOOTER_LINK_CLASS}>
        <Link to="/help" onClick={closePanel} data-testid="help-browse-all">
          Browse all help
        </Link>
      </Button>
      <ContactSupportLink />
    </div>
  );
}

function ArticlesTab() {
  const { pathname, pageName, searchQuery, setSearchQuery, chatDisabled, openArticle, openTopic, setTab, closePanel } = useHelp();
  const navigate = useNavigate();
  const searching = searchQuery.trim() !== '';

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
        <HelpSearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search all help"
          data-testid="help-search-input"
        />
        {searching ? (
          <SearchResults
            query={searchQuery}
            onSelectArticle={openArticle}
            onAsk={chatDisabled ? undefined : () => setTab('ask')}
            onContactSupport={() => {
              closePanel();
              navigate('/support');
            }}
          />
        ) : (
          <>
            <ArticleList
              pathname={pathname}
              pageName={pageName}
              onSelectArticle={openArticle}
              fallback={<CategoryCards variant="list" onSelectTopic={openTopic} />}
            />
            {!chatDisabled && (
              <section className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4" aria-labelledby="help-ask-card">
                <h3 id="help-ask-card" className="text-sm font-semibold text-foreground">
                  Can't find it?
                </h3>
                <p className="text-sm text-muted-foreground">
                  Ask Resida Help a question about using Resida. Answers come from these help articles and cite them.
                </p>
                <Button type="button" onClick={() => setTab('ask')} data-testid="help-ask-cta">
                  Ask a question
                </Button>
              </section>
            )}
          </>
        )}
      </div>
      <ArticlesFooter />
    </>
  );
}

function TopicPane({ category }: { category: string }) {
  const { openArticle } = useHelp();
  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto p-4" data-testid="help-topic-pane">
        <TopicArticles category={category} onSelectArticle={openArticle} />
      </div>
      <ArticlesFooter />
    </>
  );
}

function ArticlePane({ articleId }: { articleId: string }) {
  const { openArticle, closeArticle, chatDisabled, setTab } = useHelp();
  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto p-4" data-testid="help-article-pane">
        <ArticleView articleId={articleId} onSelectArticle={openArticle} onOpenHelpCenter={closeArticle} focusHeading />
      </div>
      <div className="flex flex-none items-center justify-between border-t border-border px-4 py-1">
        {!chatDisabled ? (
          <Button type="button" variant="ghost" className={FOOTER_LINK_CLASS} onClick={() => setTab('ask')} data-testid="help-article-ask">
            Ask a question
          </Button>
        ) : (
          <span />
        )}
        <ContactSupportLink />
      </div>
    </>
  );
}

/**
 * Keeps keyboard focus inside the panel when its view swaps, so Escape and Tab keep working: an opened
 * article moves focus to its own heading (ArticleView), a topic or the history view to the panel title, and
 * closing any of them returns focus to what opened it (the article's or topic's row, or the History button).
 */
function useViewFocus(
  articleId: string | null,
  topicCategory: string | null,
  historyOpen: boolean,
  root: RefObject<HTMLDivElement>,
  titleRef: RefObject<HTMLHeadingElement>,
  historyButtonRef: RefObject<HTMLButtonElement>,
) {
  const previous = useRef({ articleId, topicCategory, historyOpen });
  // The first article (or topic) opened from a list; hops after it still return to that row.
  const articleOpenedFrom = useRef(articleId);
  const topicOpenedFrom = useRef(topicCategory);

  useEffect(() => {
    const before = previous.current;
    previous.current = { articleId, topicCategory, historyOpen };
    const rowFor = (key: string | null) =>
      key ? root.current?.querySelector<HTMLElement>(`[data-focus-key="${CSS.escape(key)}"]`) : null;

    if (before.articleId === null && articleId !== null) articleOpenedFrom.current = articleId;
    if (before.articleId !== null && articleId === null) {
      const from = articleOpenedFrom.current;
      (rowFor(from && articleFocusKey(from)) ?? titleRef.current)?.focus();
    }

    if (before.topicCategory === null && topicCategory !== null) {
      topicOpenedFrom.current = topicCategory;
      titleRef.current?.focus();
    }
    if (before.topicCategory !== null && topicCategory === null) {
      const from = topicOpenedFrom.current;
      (rowFor(from && topicFocusKey(from)) ?? titleRef.current)?.focus();
    }

    if (!before.historyOpen && historyOpen) titleRef.current?.focus();
    if (before.historyOpen && !historyOpen) (historyButtonRef.current ?? titleRef.current)?.focus();
  }, [articleId, topicCategory, historyOpen, root, titleRef, historyButtonRef]);
}

function PanelContent({ inSheet }: { inSheet: boolean }) {
  const { tab, articleId, topic, historyOpen, chatDisabled, setTab } = useHelp();
  const rootRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  useViewFocus(articleId, topic?.category ?? null, historyOpen, rootRef, titleRef, historyButtonRef);

  let body: ReactNode;
  if (articleId) {
    body = <ArticlePane articleId={articleId} />;
  } else if (topic) {
    body = <TopicPane category={topic.category} />;
  } else if (chatDisabled) {
    body = <ArticlesTab />;
  } else if (historyOpen) {
    body = <AskPanel variant="panel" />;
  } else {
    body = (
      <Tabs
        value={tab}
        onValueChange={(next) => setTab(next as HelpTab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className="mx-4 mt-3 grid h-auto flex-none grid-cols-2 gap-1 rounded-[10px] p-1">
          <TabsTrigger value="articles" className="min-h-[44px] font-semibold" data-testid="help-tab-articles">
            Articles
          </TabsTrigger>
          <TabsTrigger value="ask" className="min-h-[44px] font-semibold" data-testid="help-tab-ask">
            Ask Resida Help
          </TabsTrigger>
        </TabsList>
        <TabsContent value="articles" className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
          <ArticlesTab />
        </TabsContent>
        <TabsContent value="ask" className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
          <AskPanel variant="panel" />
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <div ref={rootRef} className="flex min-h-0 flex-1 flex-col">
      <PanelHeader inSheet={inSheet} titleRef={titleRef} historyButtonRef={historyButtonRef} />
      {body}
    </div>
  );
}

/** The docked column (>= 1024px): fixed under the header, non-modal, no scrim. */
function DockedPanel() {
  const { closePanel } = useHelp();
  const panelRef = useRef<HTMLElement>(null);

  // The page offsets its right edge by this variable, exactly as it does for the workflow rail.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--workflow-rail-width', DOCK_WIDTH_REM);
    return () => {
      root.style.removeProperty('--workflow-rail-width');
    };
  }, []);

  // Move focus into the panel on open and give it back to the Help button on close.
  useEffect(() => {
    panelRef.current?.focus();
    return focusHelpButton;
  }, []);

  return (
    <aside
      ref={panelRef}
      id="help-panel"
      tabIndex={-1}
      aria-label="Help panel"
      data-testid="help-panel"
      onKeyDown={(event) => {
        if (event.key === 'Escape') closePanel();
      }}
      className="fixed bottom-[var(--cookie-consent-covered)] right-0 top-14 z-30 flex w-[25rem] flex-col border-l border-border bg-card shadow-sm focus:outline-none"
    >
      <PanelContent inSheet={false} />
    </aside>
  );
}

export function HelpPanel() {
  const { open, closePanel } = useHelp();
  const isDocked = useMediaQuery('(min-width: 1024px)');

  if (isDocked) return open ? <DockedPanel /> : null;

  return (
    <Sheet open={open} onOpenChange={(next) => !next && closePanel()}>
      <SheetContent
        side="bottom"
        id="help-panel"
        data-testid="help-sheet"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          focusHelpButton();
        }}
        className="bottom-[var(--cookie-consent-covered)] top-14 flex flex-col gap-0 rounded-t-2xl bg-card p-0 [&>button]:hidden"
      >
        <SheetDescription className="sr-only">Help articles and Ask Resida Help</SheetDescription>
        <div className="mx-auto mt-2 h-1 w-10 flex-none rounded-full bg-border" aria-hidden="true" />
        <PanelContent inSheet />
      </SheetContent>
    </Sheet>
  );
}
